<!--
/**
 * FormKitCharitySelect
 *
 * Custom FormKit input wrapping PrimeVue AutoComplete for charity selection.
 * Server search via useBffClient().getCharities() (name contains); loadById via getCharity().
 *
 * FormKit:
 *   <FormKit type="charitySelect" name="charityId" label="Linked charity" validation="required" />
 *
 * Standalone:
 *   <FormKitCharitySelect v-model="charityId" />
 */
-->
<template>
  <div v-bind="attrs" :aria-required="isRequired" :class="[wrapperClass]">
    <AutoComplete
      :input-id="inputId"
      :model-value="displayValue"
      :suggestions="suggestions"
      option-label="label"
      option-value="id"
      :disabled="disabled"
      :show-clear="true"
      complete-on-focus
      class="w-full"
      data-testid="formkit-charity-select-input"
      @complete="onSearch"
      @update:model-value="onSelect"
    />
  </div>
</template>

<script lang="ts" setup>
import type { FormKitFrameworkContext } from '@formkit/core'
import type { Charity } from '~/models/graphql/generated/types'
import {
  CharityFilterFieldEnum,
  StringOperatorEnum,
} from '~/models/graphql/generated/types'
import { useBffClient } from '~/composables/useBffClient'
import {
  useFormKitAutoComplete,
  type AutoCompleteOption,
} from '~/composables/useFormKitAutoComplete'

const PAGE_SIZE = 10
const MIN_SEARCH_LENGTH = 2

const props = withDefaults(
  defineProps<{
    context?: FormKitFrameworkContext
    modelValue?: string
  }>(),
  {
    context: undefined,
    modelValue: '',
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const bffClient = useBffClient()

function optionFromCharity(charity: Charity): AutoCompleteOption {
  const name =
    typeof charity.name === 'string' && charity.name.trim().length > 0
      ? charity.name.trim()
      : ''
  return { id: String(charity.id), label: name || '(No name)' }
}

async function search(query: string): Promise<AutoCompleteOption[]> {
  const trimmed = query?.trim()
  if (!trimmed || trimmed.length < MIN_SEARCH_LENGTH) return []

  try {
    const result = await bffClient.getCharities(
      1,
      PAGE_SIZE,
      [
        {
          field: CharityFilterFieldEnum.Name,
          operator: StringOperatorEnum.Contains,
          value: trimmed,
        },
      ],
      undefined
    )
    const charities = (result?.collection ?? []) as Charity[]
    return charities.map(optionFromCharity)
  } catch {
    return []
  }
}

async function loadById(id: string): Promise<AutoCompleteOption | null> {
  const charity = await bffClient.getCharity(id)
  if (charity) return optionFromCharity(charity)
  return null
}

const {
  suggestions,
  displayValue,
  disabled,
  inputId,
  isRequired,
  attrs,
  onSearch,
  onSelect,
  wrapperClass,
} = useFormKitAutoComplete({ props, emit, search, loadById })
</script>
